import React, { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormGroup,
  FormControlLabel,
  Switch,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { autoCompleteDataFetcher } from '../../services/GenericService/GenericService';
import RemoteDataAutoComplete from '../AutoComplete/CustomAutoComplete/RemoteDataAutoComplete';
import FixedOptionsAutoComplete from '../AutoComplete/CustomAutoComplete/FixedOptionsAutoComplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import useAuth from '../../hooks/useAuth';
import { ClientAutoComplete, LocationAutoComplete, PetrohubClientAutoComplete, ProjectAutoComplete } from '../AutoComplete';
import StatusSelect from '../Select/StatusSelect/StatusSelect';
import { equalsIgnoringCase } from '../../utils/stringUtils';
import ReportAddressAutoComplate from '../AutoComplete/ReportAddressAutoComplate/ReportAddressAutoComplate';
import ScenarioAutocomplate from '../AutoComplete/ScenarioAutocomplate/ScenarioAutocomplate';
import ClientApi from '../../services/api/ClientApi';
import { getUserCurrentPageName } from '../../services/AuthService/AuthService';
import { actionMap, PageName } from '../../utils/screenPermission';
import { useDispatch, useSelector } from 'react-redux';
import MaskTextFieldElement from './MaskTextFieldElement';

const getExtraFilter = (ExtraFilterKeys, fields) => {
  const _extraFilter = {};
  if (ExtraFilterKeys?.length) {
    ExtraFilterKeys?.forEach(({ PropName, ValuePath, FilterKey }) => {
      if (!PropName) {
        return;
      }

      const fieldPropObj = fields[PropName];
      let fieldValue = fieldPropObj;
      if (ValuePath) {
        if (fieldPropObj) {
          if (Array.isArray(fieldPropObj)) {
            fieldValue = fieldPropObj.map((item) => item[ValuePath]).filter((val) => val != null);
          } else {
            fieldValue = fieldPropObj[ValuePath];
          }
        } else {
          fieldValue = null;
        }
      }
      const extraFilterKey = FilterKey ?? PropName;
      _extraFilter[extraFilterKey] = fieldValue;
    });
  }
  return _extraFilter;
};

// Returns true when the given field value should be considered as "filled in".
const hasFieldValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === 'object') {
    return value.id !== null && value.id !== undefined && value.id !== '';
  }
  return true;
};

const parseDependentOnFields = (DependentOn) => {
  if (!DependentOn) {
    return [];
  }
  if (Array.isArray(DependentOn)) {
    return DependentOn.filter(Boolean);
  }
  return String(DependentOn)
    .split('|')
    .map((field) => field.trim())
    .filter(Boolean);
};

const isDisabledByDependentFields = (fields, DependentOn) => {
  const dependentOnFields = parseDependentOnFields(DependentOn);
  if (!dependentOnFields.length) {
    return false;
  }
  return !dependentOnFields.some((depField) => hasFieldValue(fields?.[depField]));
};

const TextFieldElement = ({ config, fields, dispatchValues, setValidationError }) => {
  const { i18nLabel, Text, Field, Required } = config || {};

  useEffect(() => {
    if (Required && setValidationError) {
      if (!fields[Field]) {
        setValidationError(true);
      } else {
        setValidationError(false);
      }
    }
  }, [Field, Required, fields, setValidationError]);
  return (
    <TextField
      fullWidth
      label={i18nLabel || Text}
      name={Field}
      size="small"
      required={Required}
      value={fields[Field]}
      onChange={(e) => dispatchValues(Field, e.target.value)}
    />
  );
};
const RemoteDataAutoCompleteElement = ({ config, fields, dispatchValues, setValidationError, cascadedOptions }) => {
  const { Field, Text, i18nLabel, SearchType, ServerFilter, Multiple, Required, ExtraFilterKeys = [], FreeSolo, LimitTags, isShowCheckBox, disableCloseOnSelect, DependentOn } = config || {};
  const { assignedContracts, assignedDivisions, assignedSourceSystems, assignedFleets } = useAuth();
  const isImpersonateing = useSelector((state) => state.auth.isImpersonateing);

  const preloadedOptions = cascadedOptions?.[Field];

  const isDisabled = useMemo(() => isDisabledByDependentFields(fields, DependentOn), [DependentOn, fields]);

  useEffect(() => {
    if (Required && setValidationError) {
      if (Multiple) {
        if (!fields[Field]?.length) {
          setValidationError(true);
        } else {
          setValidationError(false);
        }
      } else if (!fields[Field]) {
        setValidationError(true);
      } else {
        setValidationError(false);
      }
    }
  }, [Field, Multiple, Required, fields, setValidationError]);

  const extraFilter = useMemo(() => {
    const _extraFilter = {};
    if (ExtraFilterKeys?.length) {
      ExtraFilterKeys?.forEach(({ PropName, ValuePath, FilterKey }) => {
        if (!PropName) {
          return;
        }

        const fieldPropObj = fields[PropName];
        let fieldValue = fieldPropObj;
        if (ValuePath) {
          if (fieldPropObj) {
            if (Array.isArray(fieldPropObj)) {
              fieldValue = fieldPropObj.map((item) => item[ValuePath]).filter((val) => val != null);
            } else {
              fieldValue = fieldPropObj[ValuePath];
            }
          } else {
            fieldValue = null;
          }
        }
        const extraFilterKey = FilterKey ?? PropName;
        _extraFilter[extraFilterKey] = fieldValue;
        if (equalsIgnoringCase(PropName, 'contractIDs')) {
          _extraFilter[extraFilterKey] = assignedContracts;
        }
        if (equalsIgnoringCase(PropName, 'divisionIds')) {
          _extraFilter[extraFilterKey] = assignedDivisions;
        }
        if (equalsIgnoringCase(PropName, 'clientIDs')) {
          _extraFilter['sourceSystemIds'] = assignedSourceSystems;
        }
      });
    }
    if (equalsIgnoringCase(SearchType, 'vesselName')) {
      const isValidId = (id) => id !== null && id !== undefined && id !== '';
      const getSelectedIds = (selected) => {
        if (Array.isArray(selected)) {
          return selected.map((item) => item?.id).filter(isValidId);
        }
        return isValidId(selected?.id) ? [selected.id] : [];
      };
      if (assignedContracts?.length) {
        const selectedContractIds = getSelectedIds(fields?.ContractName);
        _extraFilter.contractID = selectedContractIds.length ? selectedContractIds : assignedContracts;
      }
      if (assignedFleets?.length) {
        const selectedFleetIds = getSelectedIds(fields?.FleetName);
        _extraFilter.FleetID = selectedFleetIds.length ? selectedFleetIds : assignedFleets;
      }
    }
    // Always scope contract lookups to the user's assigned contracts when available.
    if (equalsIgnoringCase(SearchType, 'contract') && assignedContracts?.length) {
      _extraFilter.contractIds = assignedContracts.join(',');
    }
    // While impersonating, scope fleet lookups to the impersonated user's assigned ids.
    if (isImpersonateing) {
      if (equalsIgnoringCase(SearchType, 'fleet') && assignedFleets?.length) {
        _extraFilter.fleetIds = assignedFleets.join(',');
      }
    }
    return _extraFilter;
  }, [ExtraFilterKeys, SearchType, assignedContracts, assignedDivisions, assignedSourceSystems, assignedFleets, fields, isImpersonateing]);

  return (
    <RemoteDataAutoComplete
      label={i18nLabel || Text}
      required={Required}
      placeholder="Select an option"
      name={Field}
      objectType={SearchType}
      fetcher={autoCompleteDataFetcher}
      selectedItem={fields[Field] ?? (Multiple ? [] : null)}
      onSelectedItemChanged={(val) => dispatchValues(Field, val)}
      serverFilter={ServerFilter}
      multiple={!!Multiple}
      extraFilter={extraFilter}
      freeSolo={FreeSolo}
      limitTags={LimitTags}
      optionsList={preloadedOptions?.length ? preloadedOptions : undefined}
      isShowCheckBox={isShowCheckBox}
      disableCloseOnSelect={disableCloseOnSelect}
      disabled={isDisabled}
    />
  );
};

const FixedOptionAutoCompleteElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple, Options, Required } = config || {};
  return (
    <FixedOptionsAutoComplete
      multiple={Multiple}
      label={i18nLabel || Text}
      placeholder="Select an option"
      name={Field}
      required={Required}
      options={Options}
      selectedItem={fields[Field]}
      onSelectedItemChanged={(val) => dispatchValues(Field, val)}
    />
  );
};

const ClientAutoCompleteElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple, Required, RenderOptionDecorator } = config || {};
  return (
    <ClientAutoComplete
      label={i18nLabel || Text}
      renderOptionDecorator={RenderOptionDecorator}
      multiple={Multiple}
      name="client"
      selectedItem={fields[Field] ?? (Multiple ? [] : null)}
      onValueChange={(val) => dispatchValues(Field, val)}
      required={Required}
    />
  );
};
const PetrohubClientAutoCompleteElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple, Required } = config || {};
  return (
    <PetrohubClientAutoComplete
      label={i18nLabel || Text}
      multiple={Multiple}
      name="client"
      selectedItem={fields[Field] ?? (Multiple ? [] : null)}
      onValueChange={(val) => dispatchValues(Field, val)}
      required={Required}
      renderOptionDecorator
    />
  );
};
const ReportAddressAutoComplateElement = ({ config, fields, dispatchValues }) => {
  const { assignedSourceSystems } = useAuth();
  const userPage = getUserCurrentPageName();
  const dispatch = useDispatch();

  const { Field, Text, i18nLabel, Multiple, Required, ExtraFilterKeys = [] } = config || {};
  const extraFilter = useMemo(() => {
    const filter = getExtraFilter(ExtraFilterKeys, fields);
    // Include clientIDs from scenario selection
    if (fields['clientIDs'] && fields['clientIDs'].length > 0) {
      filter['clientIDs'] = fields['clientIDs'].map((item) => item.id ?? item);
    }
    return filter;
  }, [ExtraFilterKeys, fields]);

  const dispatchFilterValuesChanged = useCallback(
    (propName, propValue) => {
      const mappedValues = propValue.map((item) => ({ id: item?.ClientID, text: item?.ClientName }));
      dispatch(
        actionMap[userPage].updateFilterValue({
          propName,
          propValue: mappedValues,
        })
      );
    },
    [dispatch, userPage]
  );
  const handleReportAddressAutoComplete = useCallback(async (val) => {
    dispatchValues(Field, val);
    try {
      if (val && val.length > 0) {
        const hasClientSelected = fields['clientIDs'] && fields['clientIDs'].length > 0;
        if (!hasClientSelected) {
          const response = await ClientApi.fetchAllClientsFuel({ reportAddressIDs: val.map((item) => item.id).join(','), pageSize: 20, sourceSystemIds: assignedSourceSystems, functionality: PageName[userPage] });
          if (response) {
            dispatchFilterValuesChanged('clientIDs', response?.Data);
          }
        }
      } else {
        dispatchFilterValuesChanged('clientIDs', []);
      }
    } catch (error) {
      console.error(error);
    }
  }, [Field, assignedSourceSystems, dispatchFilterValuesChanged, dispatchValues, fields, userPage]);
  return (
    <ReportAddressAutoComplate
      label={i18nLabel || Text}
      multiple={Multiple}
      name={Field}
      selectedItem={fields[Field] ?? (Multiple ? [] : null)}
      onValueChange={handleReportAddressAutoComplete}
      required={Required}
      renderOptionDecorator
      extraFilter={extraFilter}
    />
  );
};
const ScenarioAutocomplateElement = ({ config, fields, dispatchValues }) => {
  const { assignedSourceSystems } = useAuth();
  const userPage = getUserCurrentPageName();
  const dispatch = useDispatch();
  const { Field, Text, i18nLabel, Multiple, Required, ExtraFilterKeys = [] } = config || {};
  const extraFilter = useMemo(() => {
    const filter = getExtraFilter(ExtraFilterKeys, fields);
    // Include clientIDs from report address selection
    if (fields['clientIDs'] && fields['clientIDs'].length > 0) {
      filter['clientIDs'] = fields['clientIDs'].map((item) => item.id ?? item);
    }
    return filter;
  }, [ExtraFilterKeys, fields]);

  const dispatchFilterValuesChanged = useCallback(
    (propName, propValue) => {
      dispatch(
        actionMap[userPage].updateFilterValue({
          propName,
          propValue,
        })
      );
    },
    [dispatch, userPage]
  );
  const handleScenarioAutoComplete = useCallback(async (val) => {
    dispatchValues(Field, val);
    try {
      if (val && val.length > 0) {
        const hasClientSelected = fields['clientIDs'] && fields['clientIDs'].length > 0;
        if (!hasClientSelected) {
          const response = await ClientApi.fetchAllClientsFuel({
            scenarioIDs: val.map((item) => item.id).join(','),
            pageSize: 20,
            sourceSystemIds: assignedSourceSystems,
            functionality: PageName[userPage],
          });
          if (response) {
            dispatchFilterValuesChanged('clientIDs', response?.Data?.map((item) => ({ id: item?.ClientID, text: item?.ClientName })));
          }
        }
      } else {
        dispatchFilterValuesChanged('clientIDs', []);
      }
    } catch (error) {
      console.error(error);
    }
  }, [Field, assignedSourceSystems, dispatchFilterValuesChanged, dispatchValues, fields, userPage]);
  return (
    <ScenarioAutocomplate
      label={i18nLabel || Text}
      multiple={Multiple}
      name={Field}
      selectedItem={fields[Field] ?? (Multiple ? [] : null)}
      onValueChange={handleScenarioAutoComplete}
      required={Required}
      renderOptionDecorator
      extraFilter={extraFilter}
    />
  );
};
const StatusSelectElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple, Required } = config || {};
  return (
    <StatusSelect
      label={i18nLabel || Text}
      selectedValue={fields[Field]}
      onValueChange={(val) =>
        dispatchValues(Field, val)}
      required={Required}
      Multiple={Multiple}
    />
  );
};

const LocationAutoCompleteElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple, RenderOptionDecorator } = config || {};
  return (
    <LocationAutoComplete
      required
      renderOptionDecorator={RenderOptionDecorator}
      label={i18nLabel || Text}
      multiple={Multiple}
      name="location"
      selectedItem={fields[Field] || null}
      onValueChange={(val) => dispatchValues(Field, val)}
    />
  );
};

const ProjectAutoCompleteElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Multiple } = config || {};
  return (
    <ProjectAutoComplete
      label={i18nLabel || Text}
      multiple={Multiple}
      name="project"
      selectedItem={fields[Field] || null}
      onValueChange={(val) => dispatchValues(Field, val)}
    />
  );
};

const SwitchElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel } = config || {};
  return (
    <FormGroup>
      <FormControlLabel
        control={
          <Switch
            checked={!!fields[Field]}
            size="small"
            onChange={(e) => dispatchValues(Field, e.target.checked)}
          />
        }
        label={i18nLabel || Text}
        name={Field}
        // sx={{ pt: 1 }}
      />
    </FormGroup>
  );
};

const DropdownElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel, Options = [], Order, DefaultValue = '', Required } = config || {};
  return (
    <FormControl fullWidth size="small">
      <InputLabel id={`${Field}_${Order}`}>{i18nLabel || Text}</InputLabel>
      <Select
        labelId={`${Field}_${Order}`}
        value={fields[Field]}
        defaultValue={DefaultValue}
        label={i18nLabel || Text}
        name={Field}
        required={Required}
        onChange={(e) => dispatchValues(Field, e.target.value)}
      >
        {
          Options?.map(({ id, text }) => <MenuItem key={id} value={id}>{text}</MenuItem>)
        }
      </Select>
    </FormControl>
  );
};

const DatepickerElement = ({ config, fields, dispatchValues }) => {
  const { Field, Text, i18nLabel } = config || {};
  const { dateFormat } = useAuth();

  return (
    <DatePicker
      inputFormat={dateFormat}
      label={i18nLabel || Text}
      value={fields[Field] || null}
      renderInput={(params) => <TextField size="small" fullWidth {...params} />}
      name={Field}
      disableMaskedInput
      onChange={(date) => {
        const isDate = (date?.getTime() > 0);
        dispatchValues(Field, isDate ? date?.toISOString() : '');
      }}
    />
  );
};

export const SearchFilterItemFactory = ({ config, fields, dispatchValues, setValidationError, cascadedOptions }) => {
  const { t } = useTranslation();

  const clone = window.structuredClone(config);
  const { Field, ControlType = 'TextField' } = clone || {};
  clone['i18nLabel'] = t(`Label_${Field}`);
  const comp = {
    TextField: (
      <TextFieldElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    MaskTextField: (
      <MaskTextFieldElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    Switch: (
      <SwitchElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    RemoteDataAutoComplete: (
      <RemoteDataAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
        cascadedOptions={cascadedOptions}
      />
    ),
    Dropdown: (
      <DropdownElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    FixedOptionAutoComplete: (
      <FixedOptionAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    DatePicker: (
      <DatepickerElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    ClientAutoComplete: (
      <ClientAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    PetrohubClientAutoComplete: (
      <PetrohubClientAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    ReportAddressAutoComplate: (
      <ReportAddressAutoComplateElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    ScenarioAutocomplate: (
      <ScenarioAutocomplateElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    LocationAutoComplete: (
      <LocationAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),
    ProjectAutoComplete: (
      <ProjectAutoCompleteElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    ),

    StatusSelect: (
      <StatusSelectElement
        config={clone}
        fields={fields}
        setValidationError={setValidationError}
        dispatchValues={dispatchValues}
      />
    )
  };

  return comp[ControlType];
};
